import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { toast } from '#src/utils/toast.ts'

export type UseFhirCreateMutationArgs<T extends ResourceType> =
  ExtractResource<T>

export interface UseFhirCreateMutationOptions<T extends ResourceType> {
  mutation?: Omit<
    UseMutationOptions<
      ExtractResource<T>,
      unknown,
      UseFhirCreateMutationArgs<T>
    >,
    'mutationFn'
  >
}
/**
 * @param type - The resource type.
 * @param options - The options.
 * @returns The query result.
 * @deprecated Use useCreateMutation instead
 */
export function useFhirCreateMutation<T extends ResourceType>(
  type: T,
  options?: UseFhirCreateMutationOptions<T>
): UseMutationResult<
  ExtractResource<T>,
  unknown,
  UseFhirCreateMutationArgs<T>
> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()

  return useMutation({
    ...options?.mutation,
    onSuccess: (data, variables, context) => {
      invariant(data.id, 'Data ID is required')
      cacheKeys.invalidateMutation(queryClient, type, data.id)
      queryClient.setQueryData(cacheKeys.read(type, data.id), data)
      options?.mutation?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      console.error('Error creating resource:', error)
      toast.error(
        error instanceof Error ? error.message : 'Error creating resource',
        {
          duration: 2500,
        }
      )
      options?.mutation?.onError?.(error, variables, context)
    },
    mutationFn: (args) => {
      if (isReadOnly) {
        console.warn(
          '[read-only] mutation attempted: useFhirCreateMutation',
          type
        )
      }
      return medplum.createResource<ExtractResource<T>>(args)
    },
  })
}
